import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DataNoRowOverlayComponent, getContextMenuItems } from '@y42/ag-grid';
import { connectState } from '@y42/angular/util';
import { DateTimeFormat, DateTimeService } from '@y42/date-time';
import { ColDef, GetRowIdParams, GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject } from 'rxjs';
import { LogModel, LogTypeEnum } from '../../state/log.model';

@UntilDestroy()
@Component({
  selector: 'y42-logs-ag-grid-v2',
  templateUrl: './logs-ag-grid-v2.component.html',
  styleUrls: ['./logs-ag-grid-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogsAgGridV2Component implements OnChanges {
  constructor(private readonly dateTimeService: DateTimeService) {}

  @Input() logs: LogModel[] | null = [];

  private readonly gridLoading$$ = new BehaviorSubject(true);

  readonly badLogs = [LogTypeEnum.Warning, LogTypeEnum.Debug, LogTypeEnum.Error, LogTypeEnum.Critical];

  readonly frameworkComponents = {
    dataNoRowOverlayComponent: DataNoRowOverlayComponent,
  };

  readonly gridOptions: GridOptions = {
    suppressCellFocus: true,
    noRowsOverlayComponent: 'dataNoRowOverlayComponent',
    noRowsOverlayComponentParams: {
      text: 'No  logs Found',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    },
    getRowId: ({ data }: GetRowIdParams<LogModel>) => data.id,
    context: {
      componentParent: this,
    },
    animateRows: false,
    suppressRowClickSelection: true,
    getContextMenuItems,
  };

  readonly state = connectState(this, {
    gridLoading: this.gridLoading$$,
  });

  readonly columnDefs: ColDef[] = [
    {
      headerName: 'Level',
      field: 'level',
      filter: 'agSetColumnFilter',
      minWidth: 110,
      width: 110,
      maxWidth: 110,
      cellClass: 'flex items-center',
      cellRenderer: (params: ICellRendererParams) => {
        const badLogs = (params as ICellRendererParams & { badLogs: LogTypeEnum[] }).badLogs;
        return `<span
							class="${badLogs.includes(params.value) ? 'badge-state-failure' : 'badge-state-neutral-blue'}">${params.value}</span>`;
      },
      cellRendererParams: {
        badLogs: this.badLogs,
      },
    },
    {
      headerName: 'Message',
      field: 'message',
      filter: 'agTextColumnFilter',
      minWidth: 150,
      autoHeight: true,
    },
    {
      headerName: 'Time',
      menuTabs: [],
      maxWidth: 250,
      field: 'time',
      minWidth: 80,
      sort: 'desc',
      valueFormatter: (params) =>
        params.value
          ? this.dateTimeService.formatDate(params.value * 1000, DateTimeFormat.ShortDateTimeWithSeconds)
          : '',
    },
  ];

  onColumnResized(): void {
    setTimeout(() => this.gridLoading$$.next(false), 300);
  }

  ngOnChanges(): void {
    this.gridOptions.api?.sizeColumnsToFit();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridOptions.api = params.api;
    setTimeout(() => this.gridLoading$$.next(false), 200);
  }

  applyTransaction(data: LogModel): void {
    this.gridOptions.api?.applyTransaction({
      add: [data],
      addIndex: 0,
    });
  }
}
